import { makeAutoObservable } from 'mobx';
import * as React from 'react';
import { component } from 'tsdi';
import { observer } from 'mobx-react';
import { injectTSDI } from '../../core/tsdi';

import './overlay.scss';
import { Button } from '../button';

export interface OverlayComponentProps {
    closeOverlay: () => void;
    age?: number;
}

type OverlayEntry = {
    id: string;
    component: React.ComponentType<OverlayComponentProps>;
};

@component
export class OverlayStore {
    constructor() {
        makeAutoObservable(this);
    }

    public entries: OverlayEntry[] = [];

    public addOverlayEntry = (entry: OverlayEntry) => {
        if (!this.hasEntryWithId(entry.id)) {
            this.entries.push(entry);
        }
    };

    public hasEntryWithId = (id: string) =>
        this.entries.some((entry) => entry.id === id);

    public removeOverlayEntry = (id: string) => {
        this.entries = this.entries.filter((entry) => entry.id !== id);
    };
}

export function openOverlay(entry: OverlayEntry) {
    const store = injectTSDI(OverlayStore);
    store.addOverlayEntry(entry);
}

export const closeOverlay = (id: string) => {
    const store = injectTSDI(OverlayStore);
    store.removeOverlayEntry(id);
};

export const Overlay = observer(function Overlay() {
    const { entries, removeOverlayEntry } = injectTSDI(OverlayStore);

    const className = classnames('overlay', {
        'overlay--inactive': entries.length === 0
    });

    return (
        <section className={className}>
            <div className="overlay__bg" />
            {entries.map((entry) => {
                const { id, component: Component } = entry;
                const closeOverlay = () => removeOverlayEntry(id);
                return <Component key={id} closeOverlay={closeOverlay} />;
            })}
        </section>
    );
});

export type OverlayNotePosition = 'corner' | 'center';

interface OverlayNoteProps {
    children: React.ReactNode;
    position?: OverlayNotePosition;
}

export const OverlayNote = observer(function OverlayNote(
    props: OverlayNoteProps
) {
    const { children, position = 'corner' } = props;
    return (
        <div
            className={classnames('overlay__note', {
                'overlay__note--center': position === 'center',
                'overlay__note--corner': position === 'corner'
            })}
        >
            {children}
        </div>
    );
});

interface OverlayNoteButtonProps {
    children?: React.ReactNode;
    onClick(): void;
}

export const OverlayNoteButton = observer(function OverlayNoteButton(
    props: OverlayNoteButtonProps
) {
    return (
        <Button
            color="primary"
            fullwidth
            hover="scale"
            className="separate-2"
            onClick={props.onClick}
        >
            {props.children}
        </Button>
    );
});
